import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AltArrowDownIcon } from '@solar-icons/react/linear/alt-arrow-down'
import { cn } from '@/lib/utils/cn'

export interface DropdownOption<T extends string> {
  value: T
  label: string
  description?: string
}

export interface DropdownProps<T extends string> {
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  ariaLabel: string
  /** Shown on the trigger in place of the selected option's own label. */
  placeholder?: string
  icon?: ReactNode
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
}

/**
 * A styled listbox that replaces a native `<select>` where the trigger needs
 * its own icon, a static label, or option rows with a secondary description —
 * things a native select can't render consistently across browsers.
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  icon,
  disabled,
  size = 'sm',
  className,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const selected = options.find((option) => option.value === value)

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={selected?.label}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-line bg-surface',
          'font-medium text-muted outline-none transition-colors',
          'hover:bg-hover focus-visible:border-accent disabled:cursor-not-allowed disabled:opacity-50',
          size === 'sm' ? 'h-8 pr-2.5 pl-3 text-xs' : 'h-9.5 pr-3 pl-3.5 text-sm',
        )}
      >
        {icon}
        <span className="text-fg">{placeholder ?? selected?.label}</span>
        <AltArrowDownIcon
          size={13}
          strokeWidth={2}
          className={cn('shrink-0 text-subtle transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={ariaLabel}
          className={cn(
            'animate-rise absolute bottom-full left-0 z-20 mb-1.5 w-64 overflow-hidden',
            'rounded-xl border border-line bg-surface py-1 shadow-lg',
          )}
        >
          {options.map((option) => {
            const active = option.value === value
            return (
              <li key={option.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                    triggerRef.current?.focus()
                  }}
                  className={cn(
                    'block w-full px-3 py-2 text-left transition-colors',
                    active ? 'bg-accent-soft text-accent' : 'text-fg hover:bg-hover',
                  )}
                >
                  <span className="block text-xs font-medium">{option.label}</span>
                  {option.description && (
                    <span
                      className={cn(
                        'mt-0.5 block text-[11px]',
                        active ? 'text-accent/70' : 'text-subtle',
                      )}
                    >
                      {option.description}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
